import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

export default function ChatRoom() {
  const { username } = useParams(); // /chatroom/:username
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [socketStatus, setSocketStatus] = useState("connecting");
  const [currentUser, setCurrentUser] = useState(null);

  const socketRef = useRef(null);
  const notifySocketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
  const host = window.location.host;
  const token = localStorage.getItem("token");

  // Helper: ساخت roomName
  const getRoomName = (me, chat) => {
    if (!me || !chat) return null;
    if (chat.id === "general" || chat.is_group) return "general";
    const ids = [me.id, chat.id].sort((a, b) => a - b);
    return `private_${ids[0]}_${ids[1]}`;
  };

  // 1) دریافت پروفایل من
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await api.get("profile/");
        if (res.data && res.data.length > 0) {
          setCurrentUser(res.data[0].user_info);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchMe();
  }, []);

  // 2) دریافت مخاطبین و تنظیم چت فعال بر اساس URL
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await api.get("chat/contacts/");
        const generalChat = {
          id: "general",
          name: "تالار گفتگو",
          is_group: true,
          avatar: null,
          unread_count: 0,
        };

        const allContacts = [generalChat, ...(response.data || [])];
        setContacts(allContacts);

        if (username) {
          const targetContact = allContacts.find((c) => c.username === username);
          setActiveChat(targetContact || generalChat);
          if (!targetContact) {
            console.warn(`User ${username} not found in contacts. Falling back to general.`);
          }
        } else {
          setActiveChat(generalChat);
        }
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };

    fetchContacts();
  }, [username]);

  // 3) اتصال به سوکت نوتیفیکیشن (فقط notify، بدون roomName)
  useEffect(() => {
    if (!currentUser) return;

    const notifyUrl = `${protocol}${host}/ws/notify/${token ? `?token=${encodeURIComponent(token)}` : ""}`;

    if (notifySocketRef.current) {
      try {
        notifySocketRef.current.close();
      } catch {}
    }

    const notifyWs = new WebSocket(notifyUrl);
    notifySocketRef.current = notifyWs;

    notifyWs.onopen = () => console.log("🔔 Connected to Notification Socket");
    notifyWs.onclose = () => console.log("🔔 Notification Socket closed");
    notifyWs.onerror = (e) => console.log("🔔 Notification Socket error", e);

    notifyWs.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.message_type === "new_message") {
        const senderId = Number(data.sender_id);

        // اگر روی اون چت نیستیم، unread رو زیاد کن
        if (!activeChat || activeChat.id !== senderId) {
          setContacts((prev) =>
            prev.map((c) =>
              c.id === senderId ? { ...c, unread_count: (c.unread_count || 0) + 1 } : c
            )
          );
        }
      }
    };

    return () => {
      if (notifySocketRef.current) {
        try {
          notifySocketRef.current.close();
        } catch {}
      }
    };
  }, [currentUser, activeChat, protocol, host, token]);

  // 4) آپلود فایل
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat || !currentUser) return;

    const roomName = getRoomName(currentUser, activeChat);
    if (!roomName) return;

    const formData = new FormData();
    formData.append("image", file);
    formData.append("room_name", roomName);
    if (draft.trim()) formData.append("content", draft.trim());

    try {
      await api.post("chat/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft("");
      // بهتره بعد از آپلود تاریخچه رو هم رفرش کنی یا منتظر WS بمونی
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("خطا در ارسال تصویر");
    } finally {
      // اجازه بده دوباره همون فایل رو انتخاب کنه
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // 5) سوکت پیام‌ها + تاریخچه
  useEffect(() => {
    if (!activeChat || !currentUser) return;

    // آپدیت URL
    if (!activeChat.is_group && activeChat.username) {
      navigate(`/chatroom/${activeChat.username}`, { replace: true });
    } else if (activeChat.id === "general") {
      navigate(`/chatroom`, { replace: true });
    }

    // صفر کردن unread
    setContacts((prev) => prev.map((c) => (c.id === activeChat.id ? { ...c, unread_count: 0 } : c)));

    const roomName = getRoomName(currentUser, activeChat);
    if (!roomName) return;

    const wsUrl = `${protocol}${host}/ws/chat/${roomName}/${token ? `?token=${encodeURIComponent(token)}` : ""}`;

    // بستن سوکت قبلی
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch {}
    }

    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    const markAsRead = () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "mark_read" }));
      }
    };

    const fetchHistory = async () => {
      try {
        setMessages([]);
        const res = await api.get(`chat/history/${roomName}/`);
        const history = (res.data || []).map((msg) => ({
          me: msg.user_id === currentUser.id,
          text: msg.content,
          image: msg.image,
          time: msg.timestamp,
          sender_name: msg.sender_name,
          sender_avatar: msg.sender_avatar,
          is_read: msg.is_read,
        }));
        setMessages(history);
        markAsRead();
      } catch (err) {
        console.error("Error fetching history:", err);
      }
    };

    fetchHistory();

    ws.onopen = () => {
      setSocketStatus("open");
      markAsRead();
    };
    ws.onclose = () => setSocketStatus("closed");
    ws.onerror = () => setSocketStatus("closed");

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "chat_message") {
        const newMessage = {
          me: data.user_id === currentUser.id,
          text: data.message,
          image: data.image,
          time: new Date().toISOString(),
          sender_name: data.username,
          sender_avatar: data.user_avatar,
          is_read: false,
        };

        setMessages((prev) => [...prev, newMessage]);

        if (data.user_id !== currentUser.id) {
          markAsRead();
        }
      } else if (data.type === "read_receipt") {
        if (data.reader_id !== currentUser.id) {
          setMessages((prev) =>
            prev.map((msg) => (msg.me && !msg.is_read ? { ...msg, is_read: true } : msg))
          );
        }
      }
    };

    return () => {
      try {
        ws.close();
      } catch {}
    };
  }, [activeChat, currentUser, navigate, protocol, host, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = () => {
    const txt = draft.trim();
    if (!txt || socketStatus !== "open" || !currentUser || !socketRef.current) return;

    socketRef.current.send(
      JSON.stringify({
        type: "chat_message",
        message: txt,
        // بهتره بک‌اند user رو از token بگیره، ولی فعلاً نگه می‌داریم
        user_id: currentUser.id,
        username: currentUser.username,
      })
    );
    setDraft("");
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      <div className="container">
        <div className="row p-full">
          <div className="col-12">
            <div className="chatroom"></div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row p-full">
          <div className="col-12">
            <section className="chat-box">
              <div className="chat-list">
                {contacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    className={activeChat?.id === contact.id ? "active" : ""}
                    onClick={() => setActiveChat(contact)}
                  >
                    <img
                      src={contact.avatar || (contact.is_group ? "/img/group-icon.png" : "/img/profile.webp")}
                      alt="profile"
                      onError={(e) => (e.currentTarget.src = "/img/profile.webp")}
                    />
                    {contact.name || `${contact.first_name} ${contact.last_name}`}
                    {contact.unread_count > 0 && <span>{contact.unread_count}</span>}
                  </button>
                ))}
              </div>

              <div className="chat-wrapper">
                <div
                  style={{
                    padding: "8px 15px",
                    borderBottom: "1px solid #eee",
                    backgroundColor: "var(--clr-1)",
                    position: "relative",
                    zIndex: "1",
                    fontSize: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    color: socketStatus === "open" ? "#4caf50" : "#f44336",
                  }}
                >
                  <strong>
                    {activeChat?.name || (activeChat ? `${activeChat.first_name} ${activeChat.last_name}` : "")}
                  </strong>
                  <span>{socketStatus === "open" ? "● آنلاین" : "○ اتصال..."}</span>
                </div>

                <div className="chat-role">
                  {messages.map((m, idx) => (
                    <div key={idx} className={m.me ? "my-chat" : ""}>
                      <span>
                        {!m.me && activeChat?.is_group && (
                          <ins
                            style={{
                              display: "block",
                              fontSize: "11px",
                              color: "var(--primary-clr)",
                              marginBottom: "4px",
                              fontWeight: "bold",
                              textDecoration: "none",
                            }}
                          >
                            {m.sender_name}
                          </ins>
                        )}

                        {m.image && (
                          <div className="msg-image" style={{ marginBottom: m.text ? "5px" : "0" }}>
                            <a href={m.image} target="_blank" rel="noopener noreferrer">
                              <img
                                src={m.image}
                                alt="sent"
                                style={{ maxWidth: "200px", borderRadius: "8px", display: "block", cursor: "pointer" }}
                              />
                            </a>
                          </div>
                        )}

                        {m.text && <p>{m.text}</p>}

                        <bdi className="chat-time">{formatTime(m.time)}</bdi>

                        {m.me ? (
                          m.is_read ? (
                            <bdi title="خوانده شده">
                              <svg style={{ fill: "#4caf50" }}>
                                <use xlinkHref="#readIcon" />
                              </svg>
                            </bdi>
                          ) : (
                            <bdi title="تحویل داده شده">
                              <svg style={{ fill: "#ccc" }}>
                                <use xlinkHref="#noreadIcon" />
                              </svg>
                            </bdi>
                          )
                        ) : null}
                      </span>

                      {!m.me && (
                        <img
                          src={m.sender_avatar || "/img/profile.webp"}
                          alt=""
                          className="profile-img"
                          onError={(e) => (e.currentTarget.src = "/img/profile.webp")}
                        />
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <section className="chat-sent">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleFileSelect}
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      marginLeft: "5px",
                      padding: "0 8px",
                      background: "transparent",
                      color: "var(--txt-clr)",
                      border: "none",
                      cursor: "pointer",
                    }}
                    title="ارسال عکس"
                  >
                    <svg style={{ width: "20px", height: "20px", fill: "currentColor" }} viewBox="0 0 24 24">
                      <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
                    </svg>
                  </button>

                  <textarea
                    rows={1}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={socketStatus === "open" ? "پیام خود را بنویسید..." : "در حال برقراری ارتباط..."}
                    disabled={socketStatus !== "open"}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                  />

                  <button type="button" onClick={send} disabled={socketStatus !== "open"}>
                    ارسال
                  </button>
                </section>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}